import { EventMenuType, EventSortBy, RewardType } from '../types/events.types';
import { EventStatus } from 'routes/referral/types/event-detail-api.types';
import { EventType } from '../../../types/events/common.types';

import { rewardTypeOptions, sortByOptions, statusOptions } from '../constants';

const MAX_SEARCH_LENGTH = 200;
const MAX_PAGE_SIZE = 100;

export const orderingMap: Record<EventSortBy, string> = {
  newest: '-created_at',
  earliest: 'start_date',
  trending: '-participant_count',
  low_activity: 'participant_count',
};

const VALID_ORDERINGS = Object.values(orderingMap) as readonly string[];

type SortByKey = (typeof sortByOptions)[number]['id'];
type StatusKey = (typeof statusOptions)[number]['id'];
type RewardTypeKey = (typeof rewardTypeOptions)[number]['id'];

export const sortByMap: Record<SortByKey, EventSortBy> = {
  NEWEST: 'newest',
  EARLIEST: 'earliest',
  TRENDING: 'trending',
  LOW_ACTIVITY: 'low_activity',
};

export const statusMap: Record<StatusKey, EventStatus> = {
  ALL: EventStatus.UPCOMING,
  UPCOMING: EventStatus.UPCOMING,
  REGISTRATION_OPENS: EventStatus.REGISTRATION_OPEN,
  ONGOING: EventStatus.ONGOING,
  REWARD_DISTRIBUTION: EventStatus.REWARDS_DISTRIBUTED,
  FINISHED: EventStatus.FINISHED,
};

export const rewardTypeMap: Record<RewardTypeKey, RewardType> = {
  ALL: 'rmv',
  RMV: 'rmv',
  NFT: 'nft',
  SPINS: 'spins',
};

export const eventTypeMap: Record<EventMenuType, EventType | 'all' | 'finished'> = {
  [EventMenuType.ALL]: 'all',
  [EventMenuType.REFERRAL]: 'referral',
  [EventMenuType.TRADING]: 'trading',
  [EventMenuType.AMA]: 'ama',
  [EventMenuType.FINISHED]: 'finished',
};

const reverseStatusMap: Record<EventStatus, StatusKey> = {
  [EventStatus.UPCOMING]: 'UPCOMING',
  [EventStatus.ONGOING]: 'ONGOING',
  [EventStatus.FINISHED]: 'FINISHED',
  [EventStatus.REWARDS_DISTRIBUTED]: 'REWARD_DISTRIBUTION',
  [EventStatus.REGISTRATION_OPEN]: 'REGISTRATION_OPENS',
};

const reverseRewardTypeMap: Record<RewardType, RewardTypeKey> = {
  rmv: 'RMV',
  nft: 'NFT',
  spins: 'SPINS',
};

export const getStatusKeyFromEventStatus = (status: EventStatus): StatusKey | null => {
  return status in reverseStatusMap ? reverseStatusMap[status] : null;
};

export const getRewardTypeKeyFromRewardType = (rewardType: RewardType): RewardTypeKey | null => {
  return reverseRewardTypeMap[rewardType] ?? null;
};

export const validateSearch = (search: unknown): string | null => {
  if (!search) return null;
  const sanitized = String(search).trim().slice(0, MAX_SEARCH_LENGTH);
  return sanitized.length > 0 ? sanitized : null;
};

export const validateSortBy = (sortBy: unknown): SortByKey | null => {
  if (!sortBy || typeof sortBy !== 'string') return null;
  return sortByOptions.some((option) => option.id === sortBy) ? (sortBy as SortByKey) : null;
};

export const validateStatus = (status: unknown): EventStatus | null => {
  if (!status || typeof status !== 'string') return null;
  const statusKey = statusOptions.find((option) => option.id === status)?.id as StatusKey | undefined;
  return statusKey && statusKey !== 'ALL' ? statusMap[statusKey] ?? null : null;
};

export const validateStatuses = (statuses: unknown[]): EventStatus[] => {
  return statuses.map(validateStatus).filter((s): s is EventStatus => s !== null);
};

export const validateRewardType = (rewardType: unknown): RewardType | null => {
  if (!rewardType || typeof rewardType !== 'string') return null;
  const upper = rewardType.toUpperCase();
  const rewardTypeKey = rewardTypeOptions.find((option) => option.id === upper)?.id as RewardTypeKey | undefined;
  return rewardTypeKey && rewardTypeKey !== 'ALL' ? rewardTypeMap[rewardTypeKey] ?? null : null;
};

export const validateRewardTypes = (rewardTypes: unknown[]): RewardType[] => {
  return rewardTypes.map(validateRewardType).filter((rt): rt is RewardType => rt !== null);
};

export const validateEventType = (eventType: unknown): EventMenuType | null => {
  if (!eventType || typeof eventType !== 'string') return null;
  // Return the menu/tab identity as-is so the active tab round-trips through the URL.
  const menuType = eventType.toLowerCase() as EventMenuType;
  return Object.values(EventMenuType).includes(menuType) ? menuType : null;
};

export const validateOrdering = (ordering: unknown): string | null => {
  if (!ordering || typeof ordering !== 'string') return null;
  return VALID_ORDERINGS.includes(ordering as (typeof VALID_ORDERINGS)[number]) ? ordering : null;
};

export const validatePage = (page: unknown): number | null => {
  if (page === undefined || page === null) return null;
  const num = Number(page);
  return Number.isInteger(num) && num > 0 ? num : null;
};

export const validatePageSize = (pageSize: unknown): number | null => {
  if (pageSize === undefined || pageSize === null) return null;
  const num = Number(pageSize);
  return Number.isInteger(num) && num > 0 && num <= MAX_PAGE_SIZE ? num : null;
};

export const validateEventStatusNumber = (status: unknown): EventStatus | null => {
  if (typeof status !== 'number') return null;
  return status >= 0 && status <= 4 ? (status as EventStatus) : null;
};

export const validateEventStatusNumbers = (statuses: unknown[]): EventStatus[] => {
  return statuses.map(validateEventStatusNumber).filter((s): s is EventStatus => s !== null);
};

export const validateRewardTypeString = (rewardType: unknown): RewardType | null => {
  if (!rewardType || typeof rewardType !== 'string') return null;
  const validRewardTypes: readonly RewardType[] = ['rmv', 'nft', 'spins'] as const;
  return validRewardTypes.includes(rewardType as RewardType) ? (rewardType as RewardType) : null;
};

export const validateRewardTypeStrings = (rewardTypes: unknown[]): RewardType[] => {
  return rewardTypes.map(validateRewardTypeString).filter((rt): rt is RewardType => rt !== null);
};

export const validateBoolean = (value: unknown): boolean | null => {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return null;
};

export const validateHighlighted = (highlighted: unknown): boolean | null => {
  if (highlighted === undefined || highlighted === null) return null;
  const validated = validateBoolean(highlighted);
  return validated;
};
